import { ShoppingOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { CREATE_jwel } from "../../../Config/Config";

/* ════════════════════════════════════════════════════════════════
   DATA LAYER  (no UI here)
   Source: PURCHINVOICE_MAST (one row per purchase invoice).
   The `where` sends the INVDATE range (from -> to). Today = from & to
   are both today's date.
   Change only this block if the names differ.
   ════════════════════════════════════════════════════════════════ */
const PURCHASE_SOURCE = {
  endpoint: "/api/ERP/GetDataFromGivenTableNamewithwhere",
  table: "PURCHINVOICE_MAST",
  dateKey: "INVDATE",
  invNoKey: "INVNO",
  supplierKey: "partyname",
  qtyKey: "TOTQTY",
  amountKey: "netamt",
  cancelKey: "cancelled", // rows where this === "YES" are skipped
};

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
];

// Rolling windows ending today. For calendar week/month instead
// (Mon->today, 1st->today) swap the .subtract() lines accordingly.
const getRange = (preset) => {
  const to = dayjs();
  if (preset === "week") return [to.subtract(6, "day"), to];
  if (preset === "month") return [to.subtract(29, "day"), to];
  return [to, to];
};

const usePurchases = (preset) => {
  const tenantName = localStorage.getItem("tenantName");
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const src = PURCHASE_SOURCE;
    const [fromD, toD] = getRange(preset);
    const from = fromD.format("YYYY-MM-DD");
    const to = toD.format("YYYY-MM-DD");
    // INVDATE is a datetime (2025-03-03T00:00:00) so use "< next day"
    // to include every invoice on the last date.
    const nextDay = toD.add(1, "day").format("YYYY-MM-DD");
    const where = `${src.dateKey}>='${from}' AND ${src.dateKey}<'${nextDay}'`;

    setLoading(true);
    setError(null);
    axios
      .get(
        `${CREATE_jwel}${src.endpoint}?tableName=${src.table}&where=${where}`,
        { headers: { tenantName } },
      )
      .then((res) => {
        const rows = (res.data ?? []).filter((item) => {
          const d = String(item[src.dateKey] || "").slice(0, 10);
          return (
            String(item[src.cancelKey] || "").toUpperCase() !== "YES" &&
            d >= from &&
            d <= to
          );
        });
        setRawData(rows);
      })
      .catch((err) => {
        console.error("PurchasesTable fetch error:", err);
        setError("Couldn't load purchase data");
        setRawData([]);
      })
      .finally(() => setLoading(false));
  }, [preset, tenantName]);

  // Newest invoices first
  const data = useMemo(() => {
    const src = PURCHASE_SOURCE;
    return [...rawData].sort((a, b) => {
      const da = String(a[src.dateKey] || "");
      const db = String(b[src.dateKey] || "");
      if (da !== db) return db.localeCompare(da);
      return (
        Number(b[src.invNoKey] || 0) - Number(a[src.invNoKey] || 0)
      );
    });
  }, [rawData]);

  return { data, loading, error };
};

/* ════════════════════════════════════════════════════════════════
   UI
   ════════════════════════════════════════════════════════════════ */

// 🎨 Header colors — change these two to restyle the header quickly
const HEADER_BG = "linear-gradient(90deg,#2d1b69 0%,#7c5cbf 100%)";
const HEADER_ACCENT = "#2d1b69"; // active pill text color (match HEADER_BG)

// Row height is fixed so the body is exactly 10 rows tall (scrolls beyond that)
const ROW_H = 34;
const VISIBLE_ROWS = 10;

const COLS = [
  { label: "Inv No", w: 52 },
  { label: "Inv Date", w: 76 },
  { label: "Supplier", flex: 1 },
  { label: "Qty", w: 46, right: true },
  { label: "Net Amt", w: 84, right: true },
];

const fmtQty = (n) => {
  const v = Number(n || 0);
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
};

const fmtMoney = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const PurchasesTable = () => {
  const [preset, setPreset] = useState("today");
  const { data, loading, error } = usePurchases(preset);
  const src = PURCHASE_SOURCE;

  const totalQty = data.reduce((a, d) => a + Number(d[src.qtyKey] || 0), 0);

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 2px 14px rgba(108,92,231,0.14)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* ── Gradient header — single row ── */}
      <div
        style={{
          padding: "7px 14px",
          background: HEADER_BG,
          position: "relative",
          overflow: "hidden",
          minHeight: 38,
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {/* Decorative blobs */}
        <div
          style={{
            position: "absolute",
            right: -20,
            top: -20,
            width: 70,
            height: 70,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: -10,
            bottom: -20,
            width: 50,
            height: 50,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.05)",
            pointerEvents: "none",
          }}
        />

        {/* Left: icon + title */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flex: 1,
            minWidth: 0,
          }}
        >
          <div
            style={{
              width: 26,
              height: 26,
              borderRadius: 7,
              background: "rgba(255,255,255,0.2)",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ShoppingOutlined style={{ fontSize: 13, color: "#fff" }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                color: "#fff",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
              }}
            >
              Purchases
            </div>
            <div
              style={{
                fontSize: 9,
                color: "rgba(255,255,255,0.7)",
                whiteSpace: "nowrap",
              }}
            >
              {data.length} invoices · {fmtQty(totalQty)} qty
            </div>
          </div>
        </div>

        {/* Right: preset pill toggle + pulse dot */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              background: "rgba(0,0,0,0.18)",
              borderRadius: 20,
              padding: 3,
              gap: 2,
            }}
          >
            {PRESETS.map(({ key, label }) => (
              <div
                key={key}
                onClick={() => setPreset(key)}
                style={{ position: "relative", cursor: "pointer" }}
              >
                {preset === key && (
                  <motion.div
                    layoutId="purchase-pill"
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "#fff",
                      borderRadius: 16,
                      zIndex: 0,
                    }}
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    padding: "2px 9px",
                    fontSize: 9,
                    fontWeight: 700,
                    color:
                      preset === key ? HEADER_ACCENT : "rgba(255,255,255,0.75)",
                    borderRadius: 16,
                    transition: "color 0.2s",
                    userSelect: "none",
                  }}
                >
                  {label}
                </div>
              </div>
            ))}
          </div>

          {/* Live pulse */}
          <motion.div
            animate={{ scale: [1, 1.5, 1], opacity: [1, 0.4, 1] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#ffeaa7",
              flexShrink: 0,
            }}
          />
        </div>
      </div>

      {/* ── Column headers ── */}
      <div
        style={{
          display: "flex",
          gap: 6,
          padding: "5px 14px",
          background: "rgba(231, 199, 92, 0.12)",
          borderBottom: "1px solid rgba(108,92,231,0.15)",
          flexShrink: 0,
        }}
      >
        {COLS.map((c) => (
          <div
            key={c.label}
            style={{
              width: c.w,
              flex: c.flex,
              fontSize: 9,
              fontWeight: 800,
              color: "#6c5ce7",
              textTransform: "uppercase",
              letterSpacing: 0.6,
              textAlign: c.right ? "right" : "left",
            }}
          >
            {c.label}
          </div>
        ))}
      </div>

      {/* ── Rows (height = 10 rows, scrolls if more) ── */}
      <div
        style={{
          overflowY: "auto",
          overflowX: "hidden",
          height: ROW_H * VISIBLE_ROWS,
          flexShrink: 0,
        }}
      >
        {loading ? (
          <div style={{ padding: "16px 14px", fontSize: 11, color: "#aaa" }}>
            Loading…
          </div>
        ) : error ? (
          <div style={{ padding: "16px 14px", fontSize: 11, color: "#e17055" }}>
            {error}
          </div>
        ) : data.length === 0 ? (
          <div style={{ padding: "16px 14px", fontSize: 11, color: "#aaa" }}>
            No purchases for this period
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={preset}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.22 }}
            >
              {data.map((r, i) => (
                <motion.div
                  key={`${r[src.invNoKey]}-${r.HID ?? ""}-${i}`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(i, 12) * 0.03, duration: 0.25 }}
                  whileHover={{
                    borderLeft: "3px solid #6c5ce7",
                    paddingLeft: 11,
                  }}
                  style={{
                    display: "flex",
                    gap: 6,
                    height: ROW_H,
                    boxSizing: "border-box",
                    padding: "0 14px",
                    borderBottom: "1px solid #f4f2ff",
                    borderLeft: "3px solid transparent",
                    alignItems: "center",
                    cursor: "default",
                    background: i % 2 === 0 ? "#fff" : "#fafafa",
                    transition: "background 0.15s, border-left 0.15s",
                  }}
                >
                  <div
                    style={{
                      width: 52,
                      fontSize: 11,
                      fontWeight: 800,
                      color: "#6c5ce7",
                    }}
                  >
                    {r[src.invNoKey]}
                  </div>
                  <div
                    style={{
                      width: 76,
                      fontSize: 10,
                      color: "#555",
                      fontWeight: 600,
                    }}
                  >
                    {dayjs(r[src.dateKey]).format("DD/MM/YYYY")}
                  </div>
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 10,
                      color: "#333",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={r[src.supplierKey]}
                  >
                    {r[src.supplierKey] || "-"}
                  </div>
                  <div style={{ width: 46, textAlign: "right" }}>
                    <span
                      style={{ fontSize: 11, fontWeight: 900, color: "#43aa8b" }}
                    >
                      {fmtQty(r[src.qtyKey])}
                    </span>
                  </div>
                  <div
                    style={{
                      width: 84,
                      textAlign: "right",
                      fontSize: 10,
                      fontWeight: 800,
                      color: "#2d1b69",
                    }}
                  >
                    {fmtMoney(r[src.amountKey])}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default PurchasesTable;